import { type FC, useMemo } from "react";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  FormBuilder,
  lockSchema,
  SECRET_PLACEHOLDER,
} from "@/shared/components/Form";
import {
  useCurrentPlatformQuery,
  useConnectStripeMutation,
  useDisconnectStripeMutation,
} from "@/partner/entities/platforms";
import {
  extractMutationErrorExtension,
  interpolationFormat,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";
import { makePaymentSettingsSchema } from "../model/schemas";

/**
 * Коды отказа бекенда (extensions.code) и тексты под них. Сообщение Stripe
 * на экран не выводится ни при каких условиях: оно способно вернуть эхом
 * начало переданного secret key («Invalid API Key provided: sk_live_...»),
 * поэтому и общий случай это наш собственный текст.
 */
const CONNECT_ERROR_KEYS: Record<string, string> = {
  INVALID_STRIPE_SECRET_KEY_FORMAT:
    "paymentSettings.stripeErrors.invalidSecretKeyFormat",
  INVALID_STRIPE_PUBLISHABLE_KEY_FORMAT:
    "paymentSettings.stripeErrors.invalidPublishableKeyFormat",
  STRIPE_TEST_MODE_KEY: "paymentSettings.stripeErrors.testModeKey",
  STRIPE_KEY_MODE_MISMATCH: "paymentSettings.stripeErrors.keyModeMismatch",
  INVALID_STRIPE_SECRET_KEY: "paymentSettings.stripeErrors.invalidSecretKey",
  STRIPE_KEY_MISSING_PERMISSIONS:
    "paymentSettings.stripeErrors.missingPermissionsUnknown",
  STRIPE_API_ERROR: "paymentSettings.stripeErrors.apiError",
  STRIPE_BILLING_HAS_SUBSCRIPTIONS:
    "paymentSettings.stripeErrors.hasStripeBillingSubscriptions",
};

const DISCONNECT_ERROR_KEYS: Record<string, string> = {
  STRIPE_BILLING_HAS_SUBSCRIPTIONS:
    "paymentSettings.stripeErrors.hasStripeBillingSubscriptions",
};

const CONNECT_FALLBACK_KEY = "paymentSettings.stripeErrors.fallback";
const DISCONNECT_FALLBACK_KEY = "paymentSettings.disconnectError";

export const PaymentSettingsCard: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch } = useCurrentPlatformQuery();

  const settings = data?.currentPlatform?.paymentSettings;
  // Пока Stripe подключён, поля только показывают сохранённые значения
  // (секрет точками); редактировать можно после «Отключить».
  const locked = Boolean(settings?.connectedAt);
  const schema = useMemo(() => {
    const base = makePaymentSettingsSchema(t);
    return locked ? lockSchema(base) : base;
  }, [t, locked]);

  const [connectStripe, { loading: connecting }] = useConnectStripeMutation({
    onCompleted: () => {
      enqueueSnackbar(t("paymentSettings.connectedSnackbar"), {
        variant: "success",
      });
      refetch();
    },
    onError: (error) => {
      // Restricted key без нужных прав: бекенд называет недостающие права
      // в extensions.permissions («Webhook Endpoints: Write»), это имена из
      // дашборда Stripe, без секретов. Текст держим дольше обычного, его
      // переносят в Stripe руками.
      const permissions = extractMutationErrorExtension(error, "permissions");
      if (permissions) {
        enqueueSnackbar(
          t("paymentSettings.stripeErrors.missingPermissionsList", {
            permissions,
          }),
          { variant: "error", autoHideDuration: 20000 }
        );
        return;
      }
      enqueueSnackbar(
        t(
          resolveMutationErrorKey(
            error,
            CONNECT_ERROR_KEYS,
            CONNECT_FALLBACK_KEY
          )
        ),
        { variant: "error" }
      );
    },
  });

  const [disconnectStripe, { loading: disconnecting }] =
    useDisconnectStripeMutation({
      onCompleted: () => {
        enqueueSnackbar(t("paymentSettings.disconnectedSnackbar"), {
          variant: "success",
        });
        refetch();
      },
      onError: (error) =>
        enqueueSnackbar(
          t(
            resolveMutationErrorKey(
              error,
              DISCONNECT_ERROR_KEYS,
              DISCONNECT_FALLBACK_KEY
            )
          ),
          {
            variant: "error",
          }
        ),
    });

  return (
    <Card sx={{ mt: 2 }}>
      {(connecting || disconnecting) && <LinearProgress />}
      <CardHeader
        title={t("paymentSettings.stripeTitle")}
        action={
          settings?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="success"
                label={t("paymentSettings.stripeConnectedAt", {
                  date: interpolationFormat(
                    settings.connectedAt,
                    "dateTime",
                    i18n.language
                  ),
                })}
              />
              <Chip
                size="small"
                variant="outlined"
                label={settings.secretKeyMask}
              />
            </Stack>
          ) : (
            <Chip
              size="small"
              label={t("streamingSettings.status.notConnected")}
            />
          )
        }
      />
      <CardContent>
        {connecting && (
          <Alert
            severity="info"
            icon={<CircularProgress size={16} />}
            sx={{ mb: 2 }}
          >
            {t("paymentSettings.connecting")}
          </Alert>
        )}
        <FormBuilder
          key={locked ? "locked" : "editable"}
          formId="payment-settings-form"
          schema={schema}
          initialValues={{
            secretKey: locked ? SECRET_PLACEHOLDER : "",
            publishableKey: settings?.publishableKey ?? "",
          }}
          onSubmit={async (values) => {
            await connectStripe({
              variables: {
                input: {
                  secretKey: String(values.secretKey),
                  publishableKey: String(values.publishableKey),
                },
              },
            });
          }}
          submitButton={!locked}
          resetButton={false}
          submitButtonText={t("paymentSettings.connectButton")}
          disabled={connecting || locked}
          actionsAlign="end"
        />
        {settings?.connectedAt && (
          <>
            {disconnecting && (
              <Alert
                severity="info"
                icon={<CircularProgress size={16} />}
                sx={{ mt: 2 }}
              >
                {t("paymentSettings.disconnecting")}
              </Alert>
            )}
            <Button
              variant="outlined"
              color="error"
              sx={{ mt: 2 }}
              disabled={disconnecting}
              onClick={() => disconnectStripe()}
            >
              {t("paymentSettings.disconnectButton")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
